/**
 * Custom product builder (floor + seat templates).
 * Reliability notes:
 * - Buy button stays disabled/loading until registerBuyButton wires handlers
 * - DOM listeners are idempotent (data-cpb-bound) so variant re-init is safe
 * - Cart assembly/submit goes through ProductBuilderCart (unit-tested)
 */
var customProductBuilder_selectedLogo = null;
var customProductBuilder_selectedLogoColor = null;
var customProductBuilder_totalPriceSet = {};
var customProductBuilder_productProperties = {};
var customProductBuilder_selectedFile = null;
var customProductBuilder_seatDriverFile = null;
var customProductBuilder_seatPassengerFile = null;
var customProductBuilder_cabinFloorFile = null;
var customProductBuilder_submitInFlight = false;
var customProductBuilder_buyButtonBound = false;

document.addEventListener("DOMContentLoaded", () => {
    customProductBuilder_init(true);
    customProductBuilder_registerBuyButton();
});

///////////////////////////////////////////////////////////
// Small helpers
///////////////////////////////////////////////////////////

function customProductBuilder_bindOnce(el, flag, eventName, handler) {
    if (!el || el.dataset[flag] === "1") return false;
    el.dataset[flag] = "1";
    el.addEventListener(eventName, handler);
    return true;
}

function customProductBuilder_selectedInfoText(root) {
    if (!root) return null;
    return (
        root.querySelector(".custom-product-option-list-value-main.selected .custom-product-option-list-value-info")?.innerText?.trim() ||
        root.querySelector(".custom-product-option-list-value-info")?.innerText?.trim() ||
        null
    );
}

/** Parse theme-displayed EUR amounts like "1.234,56 €" or "365,00 €". */
function customProductBuilder_parseDisplayedPrice(text) {
    if (!text) return 0;
    const cleaned = String(text).replace(/[^\d,.-]/g, "");
    if (!cleaned) return 0;
    if (cleaned.includes(",") && cleaned.includes(".")) {
        return parseFloat(cleaned.replace(/\./g, "").replace(",", ".")) || 0;
    }
    if (cleaned.includes(",")) {
        return parseFloat(cleaned.replace(",", ".")) || 0;
    }
    return parseFloat(cleaned) || 0;
}

function customProductBuilder_scrollToLogo(targetEl) {
    setTimeout(() => {
        const el = targetEl || document.querySelector("#customProductOption_LogoAnchor");
        el?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }, 10);
}

/** Scroll so the upload control sits on the bottom edge of the viewport (clear of sticky header). */
function customProductBuilder_scrollUploadIntoView(targetEl) {
    if (!targetEl) return;
    setTimeout(() => {
        targetEl.scrollIntoView({
            behavior: "smooth",
            block: "end",
        });
    }, 10);
}

function customProductBuilder_setBuyButtonReady(button) {
    if (!button) return;
    button.classList.remove("loading");
    const spinner = button.querySelector(".loading__spinner");
    if (spinner) spinner.classList.add("hidden");
    // Keep disabled when Liquid marked sold-out / unavailable
    if (button.dataset.disabled == null) {
        button.disabled = false;
    }
}

function customProductBuilder_setBuyButtonBusy(button, busy) {
    if (!button) return;
    if (busy) {
        button.classList.add("loading");
        button.disabled = true;
        button.querySelector(".loading__spinner")?.classList.remove("hidden");
    } else {
        customProductBuilder_setBuyButtonReady(button);
    }
}

function customProductBuilder_isSeatBuilder() {
    return typeof customProductBuilder_IsSeatBuilder !== "undefined" && !!customProductBuilder_IsSeatBuilder;
}

function customProductBuilder_requiresCabinPhotos() {
    return (
        typeof customProductBuilder_RequiresCabinPhotos !== "undefined" &&
        !!customProductBuilder_RequiresCabinPhotos
    );
}

function customProductBuilder_hasSeatPhotoUploadUi() {
    return !!(
        document.querySelector("#customProductBuilder_SeatDriverUpload") ||
        document.querySelector("#customProductBuilder_SeatPassengerUpload")
    );
}

function customProductBuilder_hasCabinFloorUploadUi() {
    return !!document.querySelector("#customProductBuilder_CabinFloorUpload");
}

///////////////////////////////////////////////////////////

function customProductBuilder_ProcessAccessoriesJSON(options) {
    const bindDom = !options || options.bindDom !== false;

    try {
        const dataEl = document.querySelector("#customProductBuilder_AccessoriesProductsJSONScript");
        if (!dataEl) return;

        const data = JSON.parse(dataEl.textContent);
        customProductBuilder_AccessoriesProducts.push(
            ...(data.customProductBuilder_AccessoriesProductsJSON || [])
        );
        customProductBuilder_AccessoriesVariants.push(
            ...(data.customProductBuilder_AccessoriesVariantsJSON || [])
        );
    } catch (error) {
        console.warn("customProductBuilder_ProcessAccessoriesJSON: ", error);
    }

    customProductBuilder_cleanAccessoriesProductVariants(bindDom);
}

function customProductBuilder_cleanAccessoriesProductVariants(bindDom) {
    for (const parent of customProductBuilder_AccessoriesProducts) {
        if (parent.id > 0 && Array.isArray(parent.variants)) {
            parent.variants = parent.variants.filter((variant) =>
                customProductBuilder_AccessoriesVariants.some((v) => v.id === variant.id)
            );
        }

        if (bindDom) {
            customProductBuilder_setupParentDOM(parent);
        }
    }
}

///////////////////////////////////////////////////////////

function customProductBuilder_init(wipe = true) {
    // Always refresh accessory JSON from the (possibly updated) DOM script tag
    customProductBuilder_AccessoriesProducts = [{ id: 0 }];
    customProductBuilder_AccessoriesVariants = [];

    if (wipe === true) {
        const propertyMap = [
            ["#customProductOption_passengerSeatProperty", "passengerSeat", "Passenger Seat Type"],
            ["#customProductOption_heatingTypeProperty", "heatingType", "Heating Type"],
            ["#customProductOption_HandbrakeTypeProperty", "handbrakeType", "Handbrake Type"],
            ["#customProductOption_GearShifterProperty", "gearShifter", "Gear Shifter"],
            ["#customProductOption_middleMaterialProperty", "middleMaterial", "Middle Material"],
            ["#customProductOption_middleColorProperty", "middleColor", "Middle Color"],
        ];

        for (const [selector, key, label] of propertyMap) {
            const root = document.querySelector(selector);
            if (!root) continue;
            customProductBuilder_productProperties[key] = {
                label,
                value: customProductBuilder_selectedInfoText(root),
            };
            customProductBuilder_registerPropertyClick(root, key);
        }
    } else {
        // Drop properties whose UI blocks disappeared after a variant switch
        const stillAvailable = [];
        const availability = {
            passengerSeat: "#customProductOption_passengerSeatProperty",
            heatingType: "#customProductOption_heatingTypeProperty",
            handbrakeType: "#customProductOption_HandbrakeTypeProperty",
            gearShifter: "#customProductOption_GearShifterProperty",
            middleMaterial: "#customProductOption_middleMaterialProperty",
            middleColor: "#customProductOption_middleColorProperty",
        };
        for (const [key, selector] of Object.entries(availability)) {
            if (document.querySelector(selector)) stillAvailable.push(key);
        }
        customProductBuilder_productProperties =
            ProductBuilderCart.persistPropertiesAcrossVariantChange(
                customProductBuilder_productProperties,
                stillAvailable
            );

        // Re-sync values from currently selected DOM (source of truth after merge)
        for (const [key, selector] of Object.entries(availability)) {
            const root = document.querySelector(selector);
            if (!root || !customProductBuilder_productProperties[key]) continue;
            const selected = customProductBuilder_selectedInfoText(root);
            if (selected != null) {
                customProductBuilder_productProperties[key].value = selected;
            }
            customProductBuilder_registerPropertyClick(root, key);
        }
    }

    // Bind logo option DOM (safe to re-run: setupParentDOM is idempotent)
    customProductBuilder_ProcessAccessoriesJSON({ bindDom: true });

    // Mark logo images in the media gallery
    const logoImages = customProductBuilder_AccessoriesProducts
        .map((p) => p.featured_image)
        .filter((v) => v !== undefined);
    document.querySelectorAll(".product__media-wrapper img").forEach((image) => {
        const logoImage = logoImages.find((f) => image.src?.includes(f));
        if (!logoImage) return;
        image.classList.add("customProductBuilder_LogoImage");
    });

    if (
        typeof customProductBuilder_SeatMiddleColorImgPrefix !== "undefined" &&
        customProductBuilder_SeatMiddleColorImgPrefix
    ) {
        customProductBuilder_seatMiddleColorResetImages();
        customProductBuilder_handleMiddleColorChange();
    }

    // Use main-product line price (not the grand total) to avoid double-counting logo
    const mainLinePriceText =
        document.querySelector(
            ".custom-product-option-summary-item.main-product .custom-product-option-summary-item-price span"
        )?.innerText ||
        document.querySelector(".custom-product-option-summary-total-price")?.innerText ||
        "0";
    customProductBuilder_totalPriceSet.mainProduct =
        customProductBuilder_parseDisplayedPrice(mainLinePriceText);

    if (wipe === true) {
        customProductBuilder_selectedLogo = null;
        customProductBuilder_selectedLogoColor = null;
        customProductBuilder_selectedFile = null;

        document
            .querySelectorAll("#customProductBuilder_MainVariants .custom-product-option-list-value")
            .forEach((variant) => customProductBuilder_setupMainProductDOM(variant));
    }

    // Seat photo uploads (idempotent)
    if (document.querySelector("#customProductBuilder_SeatDriverUpload")) {
        customProductBuilder_setupSeatClick("driver");
    }
    if (document.querySelector("#customProductBuilder_SeatPassengerUpload")) {
        customProductBuilder_setupSeatClick("passenger");
    }
    // Floor cabin photo upload (selected products only)
    if (customProductBuilder_hasCabinFloorUploadUi()) {
        customProductBuilder_setupCabinFloorClick();
    }
    // Seat builder: wire the "Your Logo" upload tile (-1)
    if (
        customProductBuilder_isSeatBuilder() &&
        customProductBuilder_hasSeatPhotoUploadUi() &&
        document.querySelector("#customProductOption_LogoAnchor")
    ) {
        customProductBuilder_setupParentDOM({ id: -1 });
    }

    customProductBuilder_setupCustomLogoColorControls();
}

function customProductBuilder_setupCustomLogoColorControls() {
    const customLogoColorPresets = document.querySelectorAll(
        "#customProductOptionCustomLogoColorSelect > div"
    );
    const customLogoColorInput = document.querySelector("#customProductOptionCustomLogoColorInput");

    customLogoColorPresets.forEach((preset) => {
        customProductBuilder_bindOnce(preset, "cpbColorBound", "click", (event) => {
            const target = event.currentTarget;

            customLogoColorPresets.forEach((p) => p.classList.remove("selected"));
            if (customLogoColorInput) {
                customLogoColorInput.value = "";
                customLogoColorInput.classList.remove("selected");
            }

            customProductBuilder_selectedLogoColor = target.dataset.color ?? "Black";
            target.classList.add("selected");

            document.querySelector("#customProductOptionColorInputTitle")?.classList.remove("selected");
            document.querySelector("#customProductOptionColorPickerTitle")?.classList.add("selected");

            customProductBuilder_selectLogoColor(-1);
        });
    });

    if (customLogoColorInput) {
        customProductBuilder_bindOnce(customLogoColorInput, "cpbColorInputBound", "change", () => {
            customProductBuilder_selectedLogoColor = customLogoColorInput.value;
            customLogoColorPresets.forEach((p) => p.classList.remove("selected"));
            document.querySelector("#customProductOptionColorPickerTitle")?.classList.remove("selected");
            document.querySelector("#customProductOptionColorInputTitle")?.classList.add("selected");
            customLogoColorInput.classList.add("selected");
            customProductBuilder_selectLogoColor(-1);
        });
    }
}

///////////////////////////////////////////////////////////

function customProductBuilder_applyUploadPreview(uploadEl, file, onInvalid) {
    const main = uploadEl.querySelector(".custom-product-option-list-value-main");
    if (!file) {
        onInvalid();
        if (main) main.style.borderColor = "#ff4d4d";
        return false;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ["image/png", "image/jpeg", "application/pdf"];
    if (!ALLOWED_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
        onInvalid();
        if (main) main.style.borderColor = "#ff4d4d";
        return false;
    }

    const newImg = document.createElement("img");
    newImg.className = "customProductOption_previewFile";
    newImg.src = URL.createObjectURL(file);

    if (main) {
        const existingImg = main.querySelector("svg") || main.querySelector("img");
        existingImg?.remove();
        main.prepend(newImg);
        main.style.borderColor = "white";
    }
    return true;
}

function customProductBuilder_refreshSeatWarningVisibility() {
    const warning = document.querySelector("#customProductSeatImageWarning");
    if (!warning) return;
    const bothUploaded = !!customProductBuilder_seatDriverFile && !!customProductBuilder_seatPassengerFile;
    warning.hidden = bothUploaded;
}

function customProductBuilder_setupSeatClick(type) {
    let seatInput = null;
    let seatEl = null;

    if (type === "driver") {
        seatInput = document.querySelector("#customProductSeatDriverUpload");
        seatEl = document.querySelector("#customProductBuilder_SeatDriverUpload");
    } else if (type === "passenger") {
        seatInput = document.querySelector("#customProductSeatPassengerUpload");
        seatEl = document.querySelector("#customProductBuilder_SeatPassengerUpload");
    }

    if (!seatInput || !seatEl) return;

    customProductBuilder_bindOnce(seatEl, "cpbSeatBound", "click", function () {
        seatInput.value = "";
        seatInput.click();

        seatInput.onchange = () => {
            const file = seatInput.files && seatInput.files[0] ? seatInput.files[0] : null;
            const ok = customProductBuilder_applyUploadPreview(seatEl, file, () => {
                if (type === "driver") customProductBuilder_seatDriverFile = null;
                else customProductBuilder_seatPassengerFile = null;
                customProductBuilder_refreshSeatWarningVisibility();
            });
            if (ok) {
                if (type === "driver") customProductBuilder_seatDriverFile = file;
                else customProductBuilder_seatPassengerFile = file;
            }
            customProductBuilder_refreshSeatWarningVisibility();
        };
    });
}

function customProductBuilder_setCabinWarningVisible(isVisible) {
    const warning = document.querySelector("#customProductCabinImageWarning");
    if (!warning) return;
    warning.hidden = !isVisible;
}

function customProductBuilder_setupCabinFloorClick() {
    const input = document.querySelector("#customProductCabinFloorUpload");
    const uploadEl = document.querySelector("#customProductBuilder_CabinFloorUpload");
    if (!input || !uploadEl) return;

    customProductBuilder_bindOnce(uploadEl, "cpbCabinBound", "click", function () {
        input.value = "";
        input.click();

        input.onchange = () => {
            const file = input.files && input.files[0] ? input.files[0] : null;
            const ok = customProductBuilder_applyUploadPreview(uploadEl, file, () => {
                customProductBuilder_cabinFloorFile = null;
                customProductBuilder_setCabinWarningVisible(true);
            });
            if (ok) {
                customProductBuilder_cabinFloorFile = file;
                customProductBuilder_setCabinWarningVisible(false);
            }
        };
    });
}

function customProductBuilder_registerPropertyClick(property, propName) {
    property.querySelectorAll(".custom-product-option-list-value").forEach((option) => {
        customProductBuilder_bindOnce(option, "cpbPropBound", "click", function () {
            property.querySelectorAll(".custom-product-option-list-value-main.selected").forEach((p) => {
                p.classList.remove("selected");
            });

            if (!customProductBuilder_productProperties[propName]) {
                customProductBuilder_productProperties[propName] = { label: propName, value: null };
            }

            customProductBuilder_productProperties[propName].value =
                option.querySelector(".custom-product-option-list-value-info")?.innerText?.trim() || null;

            if (propName === "middleColor") {
                customProductBuilder_handleMiddleColorChange();
            }

            option.querySelector(".custom-product-option-list-value-main")?.classList.add("selected");
        });
    });
}

function customProductBuilder_handleMiddleColorChange() {
    const mainColor = document
        .querySelector(
            "#customProductBuilder_MainVariants .custom-product-option-list-value-main.selected .custom-product-option-list-value-info"
        )
        ?.innerText;
    const middleColor = customProductBuilder_productProperties["middleColor"]?.value;

    customProductBuilder_seatMiddleColorResetImages();

    if (!mainColor || !middleColor) return;
    if (
        typeof customProductBuilder_SeatMiddleColorImgPrefix === "undefined" ||
        !customProductBuilder_SeatMiddleColorImgPrefix
    ) {
        return;
    }

    const lookingForFileName = `${customProductBuilder_SeatMiddleColorImgPrefix}-mc_${mainColor.toLowerCase()}-cc_${middleColor.toLowerCase()}`;
    let matchingImage = null;

    document.querySelectorAll("media-gallery .product__media.media img").forEach((image) => {
        if (!matchingImage && image.src.includes(lookingForFileName)) {
            matchingImage = image;
        }
    });

    if (!matchingImage) return;

    const item = matchingImage.closest("li");
    const gallery = item?.closest("ul");
    if (!item || !gallery) return;

    gallery.prepend(item);
    item.classList.remove("customProductBuilder_SeatMiddleColorImageHidden");
}

function customProductBuilder_seatMiddleColorResetImages() {
    if (
        typeof customProductBuilder_SeatMiddleColorImgPrefix === "undefined" ||
        !customProductBuilder_SeatMiddleColorImgPrefix
    ) {
        return;
    }

    document.querySelectorAll("media-gallery .product__media.media img").forEach((img) => {
        if (!img.src.includes(customProductBuilder_SeatMiddleColorImgPrefix)) return;
        const item = img.closest("li");
        if (!item) return;
        item.classList.add("customProductBuilder_SeatMiddleColorImageHidden");
        item.closest("ul")?.append(item);
    });
}

function customProductBuilder_setupMainProductDOM(variant) {
    const variantID = variant.dataset.optionValueId;
    if (variantID == null) return;

    customProductBuilder_bindOnce(variant, "cpbVariantBound", "click", function (e) {
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
                event: e,
                target: variant,
                selectedOptionValues: [variantID.toString()],
            },
        });
    });
}

function customProductBuilder_collapseLogoChildren(scopeEl) {
    if (!scopeEl) return;

    scopeEl.querySelectorAll(".custom-product-option-list-value-children").forEach((el) => {
        el.style.height = "0px";
    });
    scopeEl.querySelectorAll(".custom-product-option-list-value-expand.expanded").forEach((el) => {
        el.classList.remove("expanded");
    });
}

function customProductBuilder_setupParentDOM(parent) {
    const parentDOM = document.querySelector(
        `.custom-product-option-list-value[data-product-id="${parent.id}"]`
    );
    if (!parentDOM) return;

    // Rebuild color children only when missing (prevents duplicates after variant merge)
    let wrapper = parentDOM.querySelector(".custom-product-option-list-value-children");
    if (parent.variants?.length > 1) {
        parentDOM.querySelector(".custom-product-option-list-value-expand")?.classList.add("visible");

        if (!wrapper) {
            wrapper = document.createElement("div");
            wrapper.classList.add("custom-product-option-list-value-children");

            for (const variant of parent.variants) {
                if (!variant?.featured_image?.src) continue;

                const el = document.createElement("div");
                el.classList.add("custom-product-option-list-value-main");
                el.dataset.variantId = variant.id;

                const img = document.createElement("img");
                img.src = variant.featured_image.src;
                img.width = 120;
                img.style.width = "120px";
                img.height = 120;
                img.style.height = "120px";
                el.appendChild(img);

                const info = document.createElement("div");
                info.classList.add("custom-product-option-list-value-info");

                const title = document.createElement("h5");
                title.innerText = variant.title;
                info.appendChild(title);

                const price = document.createElement("h6");
                price.innerText = customProductBuilder_formatPrice(variant.price);
                info.appendChild(price);

                el.appendChild(info);

                el.addEventListener("click", function () {
                    if (!customProductBuilder_selectedLogo?.variants) return;
                    customProductBuilder_selectedLogoColor =
                        customProductBuilder_selectedLogo.variants.find(
                            (v) => String(v.id) === String(el.dataset.variantId)
                        );

                    wrapper.querySelectorAll(".custom-product-option-list-value-main").forEach((child) => {
                        child.classList.remove("selected");
                    });
                    el.classList.add("selected");
                    customProductBuilder_selectLogoColor();
                });

                wrapper.appendChild(el);
            }

            parentDOM.appendChild(wrapper);
        }
    } else if (parent.variants?.length === 1 && parent.variants[0]?.featured_image?.src) {
        const img = parentDOM.querySelector("img");
        if (img) img.src = parent.variants[0].featured_image.src;
    }

    // Prefer the top-level main tile (not a child color swatch)
    const topMain =
        parentDOM.querySelector(":scope > .custom-product-option-list-value-main") ||
        parentDOM.querySelector(".custom-product-option-list-value-main");
    if (!topMain) return;

    customProductBuilder_bindOnce(topMain, "cpbParentBound", "click", function () {
        // Custom upload tiles
        if (parent.id === -1 || parent.id === -2) {
            const input = document.querySelector("#customProductOptionUpload");
            if (!input) return;
            input.value = "";
            input.click();

            input.onchange = () => {
                if (!input.files || input.files.length === 0) {
                    customProductBuilder_selectedFile = null;
                    return;
                }

                const MAX_FILE_SIZE = 10 * 1024 * 1024;
                const ALLOWED_TYPES = [
                    "image/png",
                    "image/jpeg",
                    "application/pdf",
                    "image/svg+xml",
                    "application/postscript",
                    "application/vnd.adobe.illustrator",
                ];
                const file = input.files[0];

                if (!ALLOWED_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE) {
                    customProductBuilder_selectedFile = null;
                    return;
                }

                customProductBuilder_selectedFile = file;
                customProductBuilder_customLogoUploaded(parent);
            };
            return;
        }

        customProductBuilder_selectedFile = null;
        customProductBuilder_clearCustomLogoPreview();

        const isSelected = topMain.classList.contains("selected");
        const childrenEl = parentDOM.querySelector(".custom-product-option-list-value-children");
        const expandIcon = parentDOM.querySelector(".custom-product-option-list-value-expand");
        const areChildrenExpanded =
            !!expandIcon?.classList.contains("expanded") ||
            (childrenEl && childrenEl.style.height && childrenEl.style.height !== "0px");
        const isAlreadyExpanded = isSelected && areChildrenExpanded;

        document.querySelectorAll(".product__media-wrapper img").forEach((image) => {
            const logoImage = parent.featured_image && image.src?.includes(parent.featured_image);

            if (image.classList.contains("customProductBuilder_LogoImage")) {
                const li = image.closest("li");
                if (li) li.style.display = "none";
            }

            if (!logoImage) return;
            const li = image.closest("li");
            if (li) li.style.display = "block";
        });

        let isSelectedLogoExpanding = false;

        if (parent.id === 0) {
            customProductBuilder_selectedLogo = null;
            customProductBuilder_selectedLogoColor = null;
        } else if (!isAlreadyExpanded) {
            if (isSelected && customProductBuilder_selectedLogo?.id === parent.id) {
                isSelectedLogoExpanding = true;
            }

            customProductBuilder_selectedLogo = parent;

            if (!isSelectedLogoExpanding && parent.variants?.length) {
                customProductBuilder_selectedLogoColor = parent.variants[0];
            }
        }

        customProductBuilder_collapseLogoChildren(parentDOM.parentElement);

        parentDOM.parentElement
            ?.querySelectorAll(".custom-product-option-list-value-main.selected")
            .forEach((el) => el.classList.remove("selected"));
        topMain.classList.add("selected");

        if (parent.variants?.length > 1 && !isAlreadyExpanded && childrenEl) {
            childrenEl.style.height = childrenEl.scrollHeight + "px";
            parentDOM.querySelector(".custom-product-option-list-value-expand")?.classList.add("expanded");

            if (!isSelectedLogoExpanding) {
                childrenEl.querySelectorAll(".custom-product-option-list-value-main").forEach((el) => {
                    el.classList.remove("selected");
                });
            }

            const selectedChildId = customProductBuilder_selectedLogoColor?.id;
            if (selectedChildId != null) {
                childrenEl
                    .querySelector(
                        `.custom-product-option-list-value-main[data-variant-id="${selectedChildId}"]`
                    )
                    ?.classList.add("selected");
            }

            // Keep the expanded logo at the top of the viewport so color children stay in view
            customProductBuilder_scrollToLogo(parentDOM);
        }

        if (!isAlreadyExpanded) {
            customProductBuilder_selectLogoColor();
        }
    });
}

function customProductBuilder_customLogoUploaded(parent) {
    if (!customProductBuilder_selectedFile) return;

    customProductBuilder_clearCustomLogoPreview();

    customProductBuilder_selectedLogo = parent;
    customProductBuilder_selectedLogoColor = null;

    const parentDOM = document.querySelector(
        `.custom-product-option-list-value[data-product-id="${parent.id}"]`
    );
    if (!parentDOM) return;

    customProductBuilder_collapseLogoChildren(parentDOM.parentElement);

    parentDOM.parentElement
        ?.querySelectorAll(".custom-product-option-list-value-main.selected")
        .forEach((el) => el.classList.remove("selected"));
    parentDOM.querySelector(".custom-product-option-list-value-main")?.classList.add("selected");

    const allowedMimeTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    let image = document.querySelector("#customProductOption_EmptyUploadIcon svg");
    if (allowedMimeTypes.includes(customProductBuilder_selectedFile.type)) {
        const newImg = document.createElement("img");
        newImg.className = "customProductOption_previewFile";
        newImg.src = URL.createObjectURL(customProductBuilder_selectedFile);
        image = newImg;
    }

    const temp = parentDOM.querySelector(".custom-product-option-list-value-main");
    if (temp && image) {
        const existingImg = temp.querySelector("svg") || temp.querySelector("img");
        existingImg?.remove();
        temp.prepend(image);
    }

    customProductBuilder_selectLogoColor(parent.id);
}

function customProductBuilder_clearCustomLogoPreview() {
    const defaultImg = document.querySelector("#customProductOption_EmptyUploadIcon svg");
    if (!defaultImg) return;

    const skipPreviewIDs = [
        "customProductBuilder_SeatDriverUpload",
        "customProductBuilder_SeatPassengerUpload",
        "customProductBuilder_CabinFloorUpload",
    ];

    let preview = null;
    for (const p of document.querySelectorAll(".customProductOption_previewFile")) {
        const parentID = p.closest(".custom-product-option-list-value")?.id || "";
        if (skipPreviewIDs.includes(parentID)) continue;
        preview = p;
        break;
    }

    const parent = preview?.parentElement;
    preview?.remove();
    parent?.prepend(defaultImg.cloneNode(true));
}

function customProductBuilder_registerBuyButton() {
    try {
        if (typeof customProductBuilder_SectionID === "undefined") return;
    } catch {
        return;
    }

    if (typeof ProductBuilderCart === "undefined") {
        console.error("customProductBuilder: ProductBuilderCart missing — cart script failed to load");
        return;
    }

    const addToCartButton = document.querySelector(
        `#ProductSubmitButton-${customProductBuilder_SectionID}`
    );
    if (!addToCartButton) return;

    const form = document.querySelector(`#${customProductBuilder_FormID}`);
    if (!form) return;

    // Idempotent: variant updates must not stack click handlers
    if (!customProductBuilder_buyButtonBound) {
        customProductBuilder_buyButtonBound = true;

        addToCartButton.addEventListener("click", async function (e) {
            e.preventDefault();
            e.stopPropagation();

            if (customProductBuilder_submitInFlight) return;

            const mainProductInput = form.querySelector("input[name='items[0][id]']");
            const mainProductId = mainProductInput?.value;
            if (!mainProductId) {
                console.error("customProductBuilder: missing main variant id");
                return;
            }

            const hasSeatUploads = customProductBuilder_isSeatBuilder();
            const requiresCabinPhotos =
                customProductBuilder_requiresCabinPhotos() && customProductBuilder_hasCabinFloorUploadUi();
            // Any visible option block must ship; re-sync from DOM so wiped JS state cannot omit them
            const optionBlocks = [
                ["#customProductOption_passengerSeatProperty", "passengerSeat", "Passenger Seat Type"],
                ["#customProductOption_heatingTypeProperty", "heatingType", "Heating Type"],
                ["#customProductOption_HandbrakeTypeProperty", "handbrakeType", "Handbrake Type"],
                ["#customProductOption_GearShifterProperty", "gearShifter", "Gear Shifter"],
                ["#customProductOption_middleMaterialProperty", "middleMaterial", "Middle Material"],
                ["#customProductOption_middleColorProperty", "middleColor", "Middle Color"],
            ];
            const requiredPropertyKeys = [];
            const optionRootByKey = {};
            for (const [selector, key, label] of optionBlocks) {
                const root = document.querySelector(selector);
                if (!root) continue;
                optionRootByKey[key] = root;
                requiredPropertyKeys.push(key);
                const fromDom = customProductBuilder_selectedInfoText(root);
                if (fromDom) {
                    customProductBuilder_productProperties[key] = { label, value: fromDom };
                }
            }
            const orderNote = document.querySelector(`#tti-note-${customProductBuilder_SectionID}`);

            const built = ProductBuilderCart.buildFloorSeatCartItems({
                mainVariantId: mainProductId,
                groupHash: Math.random().toString(36).slice(2, 7),
                productProperties: customProductBuilder_productProperties,
                orderNote: orderNote?.value || "",
                selectedLogo: customProductBuilder_selectedLogo,
                selectedLogoColor: customProductBuilder_selectedLogoColor,
                selectedFile: customProductBuilder_selectedFile,
                hasSeatUploads,
                requiresCabinPhotos,
                requiredPropertyKeys,
                seatDriverFile: customProductBuilder_seatDriverFile,
                seatPassengerFile: customProductBuilder_seatPassengerFile,
                cabinFloorFile: customProductBuilder_cabinFloorFile,
            });

            if (!built.ok) {
                if (built.reason === "missing_cabin_floor_photo") {
                    customProductBuilder_scrollUploadIntoView(
                        document.querySelector("#customProductBuilder_CabinFloorUpload")
                    );
                } else if (built.reason === "missing_driver_seat_photo") {
                    customProductBuilder_scrollUploadIntoView(
                        document.querySelector("#customProductBuilder_SeatDriverUpload")
                    );
                } else if (built.reason === "missing_passenger_seat_photo") {
                    customProductBuilder_scrollUploadIntoView(
                        document.querySelector("#customProductBuilder_SeatPassengerUpload")
                    );
                } else if (built.reason === "missing_required_property") {
                    customProductBuilder_scrollUploadIntoView(optionRootByKey[built.key]);
                } else if (built.reason === "missing_custom_logo_file") {
                    customProductBuilder_scrollUploadIntoView(
                        document.querySelector(
                            '#customProductBuilder_Logos .custom-product-option-list-value[data-product-id="-1"], #customProductBuilder_Logos .custom-product-option-list-value[data-product-id="-2"]'
                        ) || document.querySelector("#customProductOption_LogoAnchor")
                    );
                }
                return;
            }

            customProductBuilder_submitInFlight = true;
            customProductBuilder_setBuyButtonBusy(addToCartButton, true);

            const result = await ProductBuilderCart.submitCartAdd(built.items, mainProductId);
            if (!result.ok) {
                console.error("customProductBuilder add-to-cart failed:", result.reason, result.error);
            }

            customProductBuilder_submitInFlight = false;
            customProductBuilder_setBuyButtonBusy(addToCartButton, false);
        });
    }

    customProductBuilder_setBuyButtonReady(addToCartButton);
}

function customProductBuilder_selectLogoColor(customLogoID) {
    // Seat builder has no logo line in the price summary; floors (even with cabin photos) do
    if (customProductBuilder_isSeatBuilder()) {
        return;
    }

    document
        .querySelector(".custom-product-option-summary-list .custom-product-option-summary-item.logo")
        ?.remove();

    const list = document.querySelector(".custom-product-option-summary-list");
    const total = document.querySelector(".custom-product-option-summary-total-price");
    if (!list || !total) return;

    if (customProductBuilder_selectedLogo && (customProductBuilder_selectedLogoColor || customLogoID)) {
        let tempLogoColor = null;

        if (customLogoID === -1) {
            tempLogoColor = customProductBuilder_AccessoriesProducts.find(
                (f) => f.id === customLogoID
            )?.actualVariant;
        } else {
            tempLogoColor =
                customProductBuilder_selectedLogoColor ||
                customProductBuilder_AccessoriesProducts.find((f) => f.id === customLogoID)
                    ?.actualVariant;
        }

        if (customLogoID === -1) {
            let selectedCustomLogoColor = "Black";
            const selectedPreset = document.querySelector(
                ".custom-product-option-list-value-color-picker-presets > div.selected"
            );
            if (selectedPreset) selectedCustomLogoColor = selectedPreset.dataset.color;
            const colorInput = document.querySelector("#customProductOptionCustomLogoColorInput");
            if (colorInput?.value) selectedCustomLogoColor = colorInput.value;
            customProductBuilder_selectedLogoColor = selectedCustomLogoColor;
        }

        if (!tempLogoColor || tempLogoColor.price == null) {
            customProductBuilder_totalPriceSet.logo = 0;
            const totalPrice = Object.values(customProductBuilder_totalPriceSet).reduce(
                (sum, value) => sum + value,
                0
            );
            total.innerText = customProductBuilder_formatPrice(totalPrice * 100);
            return;
        }

        const newItem = document.createElement("div");
        newItem.classList.add("custom-product-option-summary-item", "logo");

        const name = document.createElement("div");
        name.classList.add("custom-product-option-summary-item-name");

        const h5 = document.createElement("h5");
        h5.innerText =
            customProductBuilder_selectedLogo.title ||
            (customLogoID === -1 ? TEXT_YOUR_EMBROIDERED_LOGO : TEXT_YOUR_QUILTED_LOGO);
        name.appendChild(h5);

        const h6 = document.createElement("h6");
        h6.innerText = tempLogoColor.title === "Default Title" ? "" : tempLogoColor.title || "";
        if (customLogoID === -1) {
            h6.innerText = customProductBuilder_selectedLogoColor || "Black";
        }
        name.appendChild(h6);
        newItem.appendChild(name);

        const price = document.createElement("div");
        price.classList.add("custom-product-option-summary-item-price");

        if (tempLogoColor.compare_at_price) {
            const priceSale = document.createElement("div");
            priceSale.classList.add("custom-product-option-summary-item-price-sale");
            priceSale.innerText = customProductBuilder_formatPrice(tempLogoColor.compare_at_price);
            price.appendChild(priceSale);
        }

        const priceRegular = document.createElement("span");
        priceRegular.innerText = customProductBuilder_formatPrice(tempLogoColor.price);
        price.appendChild(priceRegular);
        newItem.appendChild(price);
        list.appendChild(newItem);

        customProductBuilder_totalPriceSet.logo = parseFloat(tempLogoColor.price) / 100.0;
        const totalPrice = Object.values(customProductBuilder_totalPriceSet).reduce(
            (sum, value) => sum + value,
            0
        );
        total.innerText = customProductBuilder_formatPrice(totalPrice * 100);
    } else {
        customProductBuilder_totalPriceSet.logo = 0;
        const totalPrice = Object.values(customProductBuilder_totalPriceSet).reduce(
            (sum, value) => sum + value,
            0
        );
        total.innerText = customProductBuilder_formatPrice(totalPrice * 100);
    }
}

function customProductBuilder_formatPrice(price) {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(price / 100);
}
